import { useEffect, useState } from "react";
import { api, fmtNum } from "@/lib/api";
import { AlertTriangle, Clock, FileSignature, Gauge, UserX, CheckCircle2, Wallet, Loader2, ChevronRight } from "lucide-react";

const TONE = {
  VENCIDO: "border-red-200 bg-red-50 hover:bg-red-100/70",
  POR_VENCER: "border-amber-200 bg-amber-50 hover:bg-amber-100/70",
  PAGARE: "border-sky-200 bg-sky-50 hover:bg-sky-100/70",
  LINEA: "border-red-300 bg-red-100/60 hover:bg-red-100",
  SIN_LINEA: "border-amber-200 bg-amber-50 hover:bg-amber-100/70",
};
const ICON_TONE = {
  VENCIDO: "text-red-600",
  POR_VENCER: "text-amber-600",
  PAGARE: "text-sky-600",
  LINEA: "text-red-700",
  SIN_LINEA: "text-amber-600",
};
const TAG_TONE = {
  VENCIDO: "text-red-700",
  POR_VENCER: "text-amber-700",
  PAGARE: "text-sky-700",
  LINEA: "text-red-800",
  SIN_LINEA: "text-amber-700",
};
const ICONS = { VENCIDO: AlertTriangle, POR_VENCER: Clock, PAGARE: FileSignature, LINEA: Gauge, SIN_LINEA: UserX };
const LABEL = { VENCIDO: "VENCIDO", POR_VENCER: "POR VENCER", PAGARE: "PAGARÉ", LINEA: "LÍNEA", SIN_LINEA: "SIN LÍNEA" };

const detalle = (it) => {
  switch (it.tipo) {
    case "VENCIDO":
      return `USD ${fmtNum(it.monto)}${it.dias != null ? ` · ${it.dias} días` : ""}`;
    case "POR_VENCER":
      return `USD ${fmtNum(it.monto)} · ${it.dias <= 0 ? "vence hoy" : `en ${it.dias} día${it.dias === 1 ? "" : "s"}`}`;
    case "PAGARE":
      return `USD ${fmtNum(it.monto)} · ${it.dias} días sin firma`;
    case "LINEA":
      return `${fmtNum(it.pct)}% de uso`;
    case "SIN_LINEA":
      return `USD ${fmtNum(it.monto)} · sin línea`;
    default:
      return "";
  }
};

export const AtenderHoy = ({ mes, vendedor, fechaCorte, onCliente, onVerTodos, onCargar }) => {
  const [items, setItems] = useState([]);
  const [hasData, setHasData] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get("/report/atender-hoy", { params: { vendedor, fecha_corte: fechaCorte, periodo: mes } })
      .then((r) => {
        if (!active) return;
        setItems(r.data.items || []);
        setHasData(r.data.has_data !== false);
      })
      .catch(() => { if (active) { setItems([]); setHasData(true); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [mes, vendedor, fechaCorte]);

  return (
    <section data-testid="atender-hoy" className="rounded-2xl bg-white p-6 min-w-0">
      <h3 className="text-lg font-bold text-slate-900 mb-1 leading-snug">Clientes que requieren atención</h3>
      <p className="text-xs text-slate-500 mb-4">Qué atender hoy · vencidos, por vencer y otras alertas del sistema</p>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : !hasData ? (
        <div data-testid="atender-hoy-vacio-sin-datos" className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center">
          <Wallet className="h-6 w-6 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-600 mb-3">Cargá las cuentas a recibir para ver qué atender hoy</p>
          <button
            data-testid="atender-hoy-cargar-btn"
            onClick={onCargar}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors"
          >
            Cargar cuentas a recibir
          </button>
        </div>
      ) : items.length === 0 ? (
        <div data-testid="atender-hoy-al-dia" className="flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-8 text-emerald-700">
          <CheckCircle2 className="h-5 w-5" />
          <span className="text-sm font-semibold">Todo al día ✓</span>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {items.slice(0, 9).map((it, i) => {
              const Icon = ICONS[it.tipo] || AlertTriangle;
              const tono = it.tipo === "VENCIDO" ? "bg-[#B8402A] text-white" : it.tipo === "POR_VENCER" ? "bg-[#5FA8D9] text-[#24331F]" : "tm-negra";
              const estado = it.tipo === "VENCIDO" ? `Vencido${it.dias != null ? ` hace ${it.dias} días` : ""}`
                : it.tipo === "POR_VENCER" ? (it.dias <= 0 ? "Vence hoy" : `Vence en ${it.dias} día${it.dias === 1 ? "" : "s"}`)
                : it.tipo === "PAGARE" ? `Pagaré · ${it.dias} días sin firma` : it.tipo === "LINEA" ? `Línea al ${fmtNum(it.pct)}% de uso` : "Sin línea de crédito";
              return (
                <button
                  key={`${it.tipo}-${it.cliente}-${i}`}
                  data-testid={`atender-item-${it.tipo.toLowerCase()}`}
                  onClick={() => onCliente?.(it.cliente)}
                  title={`Abrir ${it.cliente} en Cuentas a recibir`}
                  className={`text-left rounded-[22px] p-4 flex flex-col gap-3 min-w-0 min-h-[150px] transition-opacity hover:opacity-90 ${tono}`}
                >
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider opacity-90"><Icon className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{estado}</span></div>
                  <div className="text-base font-bold leading-snug line-clamp-2 break-words flex-1" title={it.cliente}>{it.cliente}</div>
                  <div className="flex items-end justify-between gap-2 min-w-0">
                    <span className="text-[11px] opacity-80 truncate min-w-0">{it.vendedor || ""}{it.pagares != null ? ` · ${it.pagares} pagaré${it.pagares === 1 ? "" : "s"}` : ""}</span>
                    {it.tipo !== "LINEA" && <span className="text-lg font-extrabold tabular-nums whitespace-nowrap">USD {fmtNum(it.monto)}</span>}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3 text-[11px] font-semibold text-slate-500">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#B8402A]" />Vencidos</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#5FA8D9]" />Vencen en ≤7 días</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#24331F]" />Otra alerta (pagaré, línea)</span>
            </div>
            <button
              data-testid="atender-hoy-ver-todos"
              onClick={onVerTodos}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors"
            >
              Ver todos <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </>
      )}
    </section>
  );
};
